// Brief-Verlauf (E-POST) für Mieter/Eigentümer/Kontakt: was wann per Post rausging und wie weit es ist.
// Beim Öffnen werden offene Briefe einmal bei der Post abgefragt (zusätzlich zum stündlichen Abgleich).
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowClockwise, Envelope, FilePdf, FolderOpen, PencilSimple, PaperPlaneTilt, SpinnerGap, WarningCircle } from '@phosphor-icons/react';
import { Chip, ImmoButton, WidgetWert } from '../../../../components/immobilien/ui';
import { aktualisiereBriefStatus, ladeBriefeAn, ladeTestbrief, statusText, type BriefZeile } from '../../../../lib/epost/epost';
import { PostVersandDialog } from './PostVersandDialog';
import { postausgangDateiPfad } from '../../../../lib/dokumente/postausgangOrdner';
import { ladeKorrektur, type Korrektur } from '../../../../lib/epost/korrektur';
import { mitVorgaengern } from '../../../../lib/epost/postausgang';

type Art = 'mieter' | 'eigentuemer' | 'kontakt';

const datumZeit = (iso: string) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function statusFarbe(id: number | null): { fg: string; bg: string } {
  if (id === 4) return { fg: 'var(--ok)', bg: 'var(--ok-soft)' };
  if (id === 99) return { fg: 'var(--bad)', bg: 'var(--bad-soft)' };
  if (id === -1) return { fg: 'var(--ink3)', bg: 'var(--fill2)' };
  return { fg: 'var(--info)', bg: 'var(--info-soft)' };
}

export function BriefVerlauf({ art, id, name }: { art: Art; id: string; name: string }) {
  const navigate = useNavigate();
  const [briefe, setBriefe] = useState<BriefZeile[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [neu, setNeu] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [pdfLaedt, setPdfLaedt] = useState<number | null>(null);
  const [korrektur, setKorrektur] = useState<null | { brief: BriefZeile; k: Korrektur }>(null);

  const laden = useCallback(async (abgleichen: boolean) => {
    setLaedt(true); setFehler(null);
    try {
      let liste = await ladeBriefeAn(art, id);
      const offen = liste.filter((b) => b.letter_id && b.status_id !== 4 && b.status_id !== 99 && b.status_id !== -1).map((b) => b.id);
      if (abgleichen && offen.length) {
        try { await aktualisiereBriefStatus(offen); liste = await ladeBriefeAn(art, id); } catch { /* Post nicht erreichbar → alter Stand */ }
      }
      setBriefe(liste);
    } catch (e) { setFehler((e as Error).message); }
    finally { setLaedt(false); }
  }, [art, id]);

  useEffect(() => { void laden(true); }, [laden]);

  const korrigieren = async (b: BriefZeile) => {
    setMeldung(null);
    try { setKorrektur({ brief: b, k: await ladeKorrektur({ ...b, empfaenger_typ: art, empfaenger_id: id, objekt: b.objekt_id ? { id: b.objekt_id } : null }) }); }
    catch (e) { setMeldung((e as Error).message); }
  };

  const testbrief = async (letterId: number) => {
    setPdfLaedt(letterId);
    try {
      const r = await ladeTestbrief(letterId);
      if (!r.pdf_base64) throw new Error('Testbrief noch nicht verarbeitet – in ein paar Minuten erneut versuchen.');
      const bytes = Uint8Array.from(atob(r.pdf_base64), (c) => c.charCodeAt(0));
      window.open(URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })), '_blank', 'noopener');
    } catch (e) { setMeldung((e as Error).message); }
    finally { setPdfLaedt(null); }
  };

  return (
    <section className="flex flex-col" style={{ gap: 12 }} aria-label="Briefe per Post">
      <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
        <span className="flex-1" style={{ fontSize: 13, color: 'var(--ink3)' }}>Per E-POST verschickte Briefe an {name}</span>
        <ImmoButton icon={laedt ? SpinnerGap : ArrowClockwise} onClick={() => void laden(true)} disabled={laedt}>Stand abfragen</ImmoButton>
        <ImmoButton icon={PaperPlaneTilt} onClick={() => setNeu(true)}>Nur Brief</ImmoButton>
        <ImmoButton art="akzent" icon={PaperPlaneTilt} onClick={() => navigate(`/admin/schreiben/neu?empfaenger=${art}:${encodeURIComponent(id)}`)}>Neues Schreiben</ImmoButton>
      </div>
      {meldung && <span role="status" style={{ fontSize: 12.5, color: 'var(--ink2)' }}>{meldung}</span>}
      {fehler && <span role="alert" className="flex items-center" style={{ gap: 6, fontSize: 12.5, color: 'var(--bad)' }}><WarningCircle weight="fill" size={14} />{fehler}</span>}
      {briefe === null ? (
        <div className="animate-pulse" style={{ height: 120, borderRadius: 18, background: 'var(--fill2)' }} aria-busy="true" />
      ) : briefe.length === 0 ? (
        <div className="flex flex-col items-center text-center" style={{ gap: 6, padding: 24, borderRadius: 18, background: 'var(--fill)' }}>
          <Envelope size={26} color="var(--ink3)" />
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch kein Brief per Post an {name}.</span>
        </div>
      ) : (
        <ul className="m-0 flex flex-col p-0" style={{ gap: 8, listStyle: 'none' }}>
          {mitVorgaengern(briefe).map(({ brief: b, vorgaenger }) => {
            const f = statusFarbe(b.status_id);
            return (
              <li key={b.id} className="flex flex-col" style={{ gap: 6, padding: '12px 14px', borderRadius: 16, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
                <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
                  <span className="min-w-0 flex-1 truncate" style={{ fontSize: 14, fontWeight: 700 }}>{b.betreff || 'Brief'}</span>
                  {b.test && <Chip groesse="xs">Test</Chip>}
                  <span style={{ height: 24, padding: '0 10px', borderRadius: 12, display: 'inline-flex', alignItems: 'center', fontSize: 11.5, fontWeight: 800, background: f.bg, color: f.fg }}>
                    {statusText(b.status_id)}
                  </span>
                </div>
                <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>
                  {datumZeit(b.created_at)}
                  {b.seiten ? ` · ${b.seiten} ${b.seiten === 1 ? 'Seite' : 'Seiten'}` : ''}
                  {b.gedruckt_at ? ` · versendet ${datumZeit(b.gedruckt_at)}` : ''}
                  {b.letter_id ? ` · Nr. ${b.letter_id}` : ''}
                </span>
                {b.einschreiben && (
                  <span style={{ fontSize: 12.5 }}>
                    {b.einschreiben}{b.einschreiben_nr ? ` · ${b.einschreiben_nr}` : ' · Nummer folgt nach dem Druck'}{b.einschreiben_status ? ` · ${b.einschreiben_status}` : ''}
                  </span>
                )}
                {b.status_id === 99 && b.fehler?.length ? (
                  <span style={{ fontSize: 12.5, color: 'var(--bad)' }}>{b.fehler.map((x) => [x.code, x.description].filter(Boolean).join(' ')).join('; ')}</span>
                ) : null}
                {b.status_id === 99 && !b.ersetzt_durch && (
                  <div>
                    <ImmoButton groesse={32} art="akzent" icon={PencilSimple} onClick={() => void korrigieren(b)}>Korrigieren und neu senden</ImmoButton>
                  </div>
                )}
                {b.ablage_datei_id && (
                  <div>
                    <ImmoButton groesse={32} icon={FolderOpen} onClick={() => navigate(b.objekt_id ? `/admin/dokumente/${encodeURIComponent(b.ablage_datei_id!)}` : postausgangDateiPfad(b.ablage_datei_id!))}>{b.objekt_id ? 'In der Akte' : 'Im Postausgang-Ordner'}</ImmoButton>
                  </div>
                )}
                {b.test && b.letter_id && (
                  <div>
                    <ImmoButton groesse={32} icon={pdfLaedt === b.letter_id ? SpinnerGap : FilePdf} onClick={() => void testbrief(b.letter_id!)} disabled={pdfLaedt !== null}>
                      Testbrief ansehen
                    </ImmoButton>
                  </div>
                )}
                {vorgaenger.length > 0 && (
                  <details style={{ fontSize: 12.5, color: 'var(--ink3)' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
                      {vorgaenger.length === 1 ? '1 frühere Fassung (abgelehnt, korrigiert)' : `${vorgaenger.length} frühere Fassungen (abgelehnt, korrigiert)`}
                    </summary>
                    <ul className="m-0 flex flex-col p-0" style={{ gap: 6, listStyle: 'none', marginTop: 6, paddingLeft: 12, borderLeft: '2px solid var(--line)' }}>
                      {vorgaenger.map((v) => (
                        <li key={v.id} className="flex flex-col" style={{ gap: 2 }}>
                          <span className="immo-zahl">{datumZeit(v.created_at)}{v.letter_id ? ` · Nr. ${v.letter_id}` : ''} · {statusText(v.status_id)}</span>
                          {v.fehler?.length ? <span style={{ color: 'var(--bad)' }}>{v.fehler.map((x) => [x.code, x.description].filter(Boolean).join(' ')).join('; ')}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {korrektur && (
        <PostVersandDialog anlagen={korrektur.k.anlagen}
          start={{ treffer: korrektur.k.treffer ?? { art, id, name, sub: '' }, anschrift: korrektur.k.anschrift, betreff: korrektur.k.betreff, text: korrektur.k.text || undefined,
            mitAnschreiben: korrektur.k.mitAnschreiben, objektId: korrektur.k.objektId, optionen: korrektur.k.optionen, ersetzt: korrektur.brief.id, hinweis: korrektur.k.hinweis }}
          bezug={korrektur.brief.bezug_typ ? { typ: korrektur.brief.bezug_typ, id: korrektur.brief.bezug_id } : null}
          onSchliessen={() => setKorrektur(null)} onGesendet={(m) => { setKorrektur(null); setMeldung(`Korrigiert neu gesendet. ${m}`); void laden(false); }} />
      )}
      {neu && (
        <PostVersandDialog anlagen={[]} start={{ treffer: { art, id, name, sub: '' }, mitAnschreiben: true }}
          onSchliessen={() => setNeu(false)} onGesendet={(m) => { setNeu(false); setMeldung(m); void laden(false); }} />
      )}
    </section>
  );
}

/** Kurzinfo fürs Widget: Anzahl Briefe und Stand des letzten. */
export function BriefeWidgetWert({ art, id }: { art: Art; id: string }) {
  const [briefe, setBriefe] = useState<BriefZeile[] | null>(null);
  // Korrigierte Fassungen zählen als ein Brief
  useEffect(() => { void ladeBriefeAn(art, id).then((l) => setBriefe(mitVorgaengern(l).map((x) => x.brief)), () => setBriefe([])); }, [art, id]);
  const letzter = briefe?.[0];
  return <WidgetWert wert={briefe ? briefe.length : '–'} sub={letzter ? `${letzter.betreff || 'Brief'} · ${statusText(letzter.status_id)}` : 'Noch kein Brief per Post'} />;
}
